import React, { useState } from 'react';
import { X, Calendar, MapPin, User, Car, DollarSign, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ReservationDetailsModal = ({ isOpen, onClose, reservation, onStatusUpdate }) => {
    const [updating, setUpdating] = useState(false);

    if (!isOpen || !reservation) return null;

    const statusConfig = {
        pending: { label: 'En attente', color: 'yellow', icon: Clock },
        confirmed: { label: 'Confirmée', color: 'blue', icon: CheckCircle },
        active: { label: 'Active', color: 'green', icon: CheckCircle },
        completed: { label: 'Terminée', color: 'gray', icon: CheckCircle },
        cancelled: { label: 'Annulée', color: 'red', icon: XCircle }
    };

    const currentStatus = statusConfig[reservation.status] || statusConfig.pending;

    const handleStatusChange = async (newStatus) => {
        setUpdating(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `http://localhost:8001/api/agencies/my/reservations/${reservation.id}/status?status=${newStatus}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Erreur lors de la mise à jour du statut');
            }

            toast.success(`Statut mis à jour: ${statusConfig[newStatus].label}`);
            onStatusUpdate();
            onClose();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Erreur lors de la mise à jour du statut');
        } finally {
            setUpdating(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const StatusIcon = currentStatus.icon;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
                {/* Header */}
                <div className={`sticky top-0 bg-gradient-to-r from-${currentStatus.color}-600 to-${currentStatus.color}-700 p-6 flex justify-between items-center rounded-t-2xl`}>
                    <div className="flex items-center gap-3">
                        <StatusIcon className="w-8 h-8 text-white" />
                        <div>
                            <h2 className="text-2xl font-bold text-white">Détails de la réservation</h2>
                            <p className="text-white/80 text-sm">#{reservation.id?.substring(0, 8)}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Status Badge */}
                    <div className="flex items-center justify-between">
                        <div className={`px-4 py-2 bg-${currentStatus.color}-500/20 border border-${currentStatus.color}-500/30 rounded-full text-${currentStatus.color}-300 font-medium flex items-center gap-2`}>
                            <StatusIcon className="w-4 h-4" />
                            {currentStatus.label}
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-gray-400">Montant total</p>
                            <p className="text-2xl font-bold text-white">{reservation.amount?.toLocaleString()} DZD</p>
                        </div>
                    </div>

                    {/* Client Info */}
                    <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <User className="w-5 h-5 text-blue-400" />
                            Informations client
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-400">Nom</p>
                                <p className="text-white font-medium">{reservation.client_name || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-400">Email</p>
                                <p className="text-white font-medium">{reservation.client_email || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-400">Téléphone</p>
                                <p className="text-white font-medium">{reservation.client_phone || 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Vehicle Info */}
                    <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <Car className="w-5 h-5 text-purple-400" />
                            Véhicule
                        </h3>
                        <p className="text-xl font-bold text-white">{reservation.vehicle || 'N/A'}</p>
                    </div>

                    {/* Dates & Locations */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-green-400" />
                                Dates
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-sm text-gray-400">Prise en charge</p>
                                    <p className="text-white font-medium">{formatDate(reservation.pickup_date)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-400">Retour</p>
                                    <p className="text-white font-medium">{formatDate(reservation.return_date)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <MapPin className="w-5 h-5 text-red-400" />
                                Lieux
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-sm text-gray-400">Lieu de prise en charge</p>
                                    <p className="text-white font-medium">{reservation.pickup_location || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-400">Lieu de retour</p>
                                    <p className="text-white font-medium">{reservation.return_location || 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    {reservation.customer_notes && (
                        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-2">Notes du client</h3>
                            <p className="text-gray-300">{reservation.customer_notes}</p>
                        </div>
                    )}

                    {/* Status Actions */}
                    <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                        <h3 className="text-lg font-semibold text-white mb-4">Changer le statut</h3>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            {Object.entries(statusConfig).map(([status, config]) => {
                                const Icon = config.icon;
                                return (
                                    <button
                                        key={status}
                                        onClick={() => handleStatusChange(status)}
                                        disabled={updating || reservation.status === status}
                                        className={`px-4 py-3 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${reservation.status === status
                                                ? `border-${config.color}-500 bg-${config.color}-500/20 text-${config.color}-300`
                                                : `border-gray-700 bg-gray-800/50 text-gray-400 hover:border-${config.color}-500 hover:text-${config.color}-300`
                                            }`}
                                    >
                                        <Icon className="w-5 h-5 mx-auto mb-1" />
                                        <span className="text-xs block">{config.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReservationDetailsModal;
